import { IconNames } from '@blueprintjs/icons';
import { sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FormSection } from '@strata/react-form-section';
import { SuccessToast } from '@strata/react-toast-message';
import Decimal from 'decimal.js';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { getPreBids, putTogglePreBid } from 'api/auctionsAPI';
import { getFirstRefusalClaims, putFirstRefusalClaim } from 'api/clientAPI';
import { ApplyTemplate } from 'components/admin/automations/ApplyTemplate';
import { AutomationTips } from 'components/admin/automations/AutomationTips';
import { UnsavedChangesDialog } from 'components/admin/automations/dialogs/UnsavedChangesDialog';
import { RuleHistory } from 'components/admin/automations/RuleHistory';
import { RulesSidebar } from 'components/admin/automations/RulesSidebar';
import { PatientTemplate } from 'components/admin/automations/templates/PatientTemplate';
import { ProductTemplate } from 'components/admin/automations/templates/ProductTemplate';
import { usePermissionCheck } from 'services/APIHooks';
import {
    type AutomationRule,
    AutomationRuleActions,
    AutomationTemplateTypes,
} from 'services/AutomationRuleHelpers';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { type PayerSegment, useGetPayerSegmentsForSupplierQuery } from 'store/api/referenceApi';

const TemplateComponentMap = {
    [AutomationTemplateTypes.PATIENT]: PatientTemplate,
    [AutomationTemplateTypes.PRODUCT]: ProductTemplate,
};

const fetchAllRules = async (npi: string, segmentList?: PayerSegment[]) =>
    segmentList
        ? (
              await Promise.all([
                  ...segmentList.map(async (segment) =>
                      (
                          await getFirstRefusalClaims({
                              npi,
                              payerId: segment.payerId,
                              segmentId: segment.id,
                          })
                      )?.map<AutomationRule>((rule) => ({
                          id: rule.first_refusal_claim_id,
                          isActive: rule.is_active,
                          template: AutomationTemplateTypes.PATIENT,
                          payerId: segment.payerId,
                          segmentId: rule.segment_id,
                          productServiceOid: rule.product_service_oid,
                          memberFirstName: rule.firstname,
                          memberId: rule.member_id,
                          memberDOB: rule.dateofbirth,
                          action: { type: AutomationRuleActions.SEND_REFERRAL },
                          description: rule.description,
                      })),
                  ),
                  (async () =>
                      (await getPreBids({ npi }))?.map<AutomationRule>((rule) => ({
                          id: rule.bid_id,
                          isActive: rule.is_active,
                          template: rule.member_id
                              ? AutomationTemplateTypes.PATIENT
                              : AutomationTemplateTypes.PRODUCT,
                          payerId: rule.payer_id,
                          segmentId: rule.segment_id,
                          productServiceOid: rule.product_service_oid,
                          // Cast null optional criteria to undefined for detecting duplicates
                          memberFirstName: rule.firstname ?? undefined,
                          memberId: rule.member_id ?? undefined,
                          memberDOB: rule.date_of_birth ?? undefined,
                          performance: rule.performance ?? undefined,
                          placeOfService: rule.place_of_service ?? undefined,
                          prescriberListId: rule.prescriber_list_id ?? undefined,
                          stateListId: rule.state_list_id ?? undefined,
                          action: {
                              type: AutomationRuleActions.SET_MAX_BID,
                              value: rule.bid_amts.map((bid) => ({
                                  bidAmt: Decimal.mul(bid.bid_amt, 100).toNumber(),
                                  referentPriceType: bid.referent_price_type,
                              })),
                          },
                          startDate: rule.start_date,
                          endDate: rule.end_date,
                          description: rule.description,
                          isBrand: rule.is_brand,
                      })))(),
              ])
          ).flat()
        : undefined;

export const Automations = () => {
    const { ruleId } = useParams() as { ruleId?: string };
    const userCanEdit = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);

    const { data: segmentList } = useGetPayerSegmentsForSupplierQuery({
        supplierNpi: selectedNpi!,
    });
    const [rules, setRules] = useState<AutomationRule[]>();
    useEffect(() => {
        void (async () => {
            try {
                setRules(await fetchAllRules(selectedNpi!, segmentList));
            } catch (err) {
                handleError('An error occurred loading automation rules.', err as Error);
            }
        })();
    }, [selectedNpi, segmentList]);

    const [activeRule, setActiveRule] = useState<Partial<AutomationRule>>();
    const ActiveTemplateComponent =
        activeRule?.template && TemplateComponentMap[activeRule.template];
    // If ruleId is present in the url, set the initial activeRule
    useEffect(() => {
        if (ruleId && rules && !activeRule) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setActiveRule(rules.find((rule) => rule.id === ruleId));
        }
    }, [ruleId, rules, activeRule]);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // Used to store rule for unsaved changes dialog
    const [pendingRule, setPendingRule] = useState<Partial<AutomationRule>>();

    const toggleRuleIsActive = (rule: AutomationRule) => {
        void (async () => {
            const { id, description, action, payerId, segmentId, isActive } = rule;
            try {
                setRules((prev) =>
                    prev?.map((r) => (r.id === id ? { ...r, isActive: !isActive } : r)),
                );
                if (action.type === AutomationRuleActions.SET_MAX_BID) {
                    await putTogglePreBid({ npi: selectedNpi!, id });
                }
                if (action.type === AutomationRuleActions.SEND_REFERRAL) {
                    await putFirstRefusalClaim({
                        npi: selectedNpi!,
                        id,
                        payerId,
                        segmentId,
                        isActive: !isActive,
                    });
                }
                SuccessToast({
                    message: `${description} was turned ${!isActive ? 'on' : 'off'} successfully.`,
                });
            } catch (err) {
                handleError(
                    `An error occurred toggling ${description}. Please try again.`,
                    err as Error,
                );
            } finally {
                const result = await fetchAllRules(selectedNpi!, segmentList);
                setRules(result);
                setActiveRule((prev) => result?.find((r) => r.id === prev?.id));
            }
        })();
    };

    return (
        <FormHeaderAndContent
            mainContent={
                <div className="sds_automations">
                    <UnsavedChangesDialog
                        isOpen={!!pendingRule}
                        onSecondaryButtonClick={() => setPendingRule(undefined)}
                        onPrimaryButtonClick={() => {
                            setActiveRule(pendingRule);
                            setPendingRule(undefined);
                        }}
                    />
                    <div
                        className="sds_automations__column"
                        style={{ width: '25%', minWidth: 260 }}
                    >
                        <FormSection
                            sectionTitle="Templates"
                            content={
                                <div className="sds_automations__template-buttons">
                                    {Object.values(AutomationTemplateTypes)
                                        .sort(sortAsc)
                                        .map((template) => (
                                            <ActionButtonText
                                                key={template}
                                                label={`${template} Template`}
                                                leftIcon={IconNames.ADD}
                                                onClick={() =>
                                                    hasUnsavedChanges &&
                                                    !activeRule?.id &&
                                                    template !== activeRule?.template
                                                        ? setPendingRule({ template })
                                                        : setActiveRule({ template })
                                                }
                                                disabled={!userCanEdit}
                                            />
                                        ))}
                                </div>
                            }
                        />
                        <div className="sds_automations__column-section--expand">
                            <RulesSidebar
                                rules={rules}
                                onRuleClick={(rule) =>
                                    hasUnsavedChanges && activeRule?.id !== rule.id
                                        ? setPendingRule(rule)
                                        : setActiveRule(rule)
                                }
                                onRuleToggle={toggleRuleIsActive}
                                userCanEdit={userCanEdit}
                            />
                        </div>
                    </div>
                    <div className="sds_automations__column" style={{ flexGrow: 1 }}>
                        {ActiveTemplateComponent ? (
                            <ActiveTemplateComponent
                                rule={activeRule}
                                rules={rules}
                                userCanEdit={userCanEdit}
                                onChange={(unsavedChanges) => setHasUnsavedChanges(unsavedChanges)}
                                onChangeRule={(newRule) =>
                                    hasUnsavedChanges
                                        ? setPendingRule(newRule)
                                        : setActiveRule(newRule)
                                }
                                onSubmitSuccess={async (id) => {
                                    const newRules = await fetchAllRules(selectedNpi!, segmentList);
                                    setRules(newRules);
                                    setActiveRule(newRules?.find((rule) => rule.id === id));
                                    SuccessToast({ message: 'Rule saved successfully.' });
                                }}
                                onDeleteSuccess={async (id) => {
                                    setActiveRule(undefined);
                                    setRules((prev) => prev?.filter((rule) => rule.id !== id));
                                    setRules(await fetchAllRules(selectedNpi!, segmentList));
                                    SuccessToast({ message: 'Rule deleted successfully.' });
                                }}
                                onDuplicate={(duplicatedRule) => {
                                    setActiveRule(duplicatedRule);
                                    SuccessToast({ message: 'Rule duplicated successfully.' });
                                }}
                            />
                        ) : (
                            <ApplyTemplate />
                        )}
                    </div>
                    <div className="sds_automations__column" style={{ width: '25%' }}>
                        {activeRule ? <RuleHistory rule={activeRule} /> : <AutomationTips />}
                    </div>
                </div>
            }
        />
    );
};
